import React from 'react'
import pluralize from 'pluralize'
import { useConfirmationModal } from 'src/components/ModalForms/useConfirmationModal'
import { capitalizeFirstLetter } from 'src/Utils/globalHelpers'
import { useIsSearchActive } from 'src/Utils/search/search.hooks'
import { useClearFilters } from './FilterBar/FilterBar.hooks'

// A filter or search narrows the list an index page shows.
export const useIsListNarrowed = (): boolean => {
  const { hasAppliedFilters } = useClearFilters()
  const isSearchActive = useIsSearchActive()
  return hasAppliedFilters || isSearchActive
}

// "the 3 risks that match your current filters and search"
export const MatchingRowsText = ({
  count,
  noun,
}: {
  count: number
  // Singular and lowercase, e.g. 'risk'.
  noun: string
}): JSX.Element => (
  <>
    the <strong>{count}</strong> {pluralize(noun, count)}{' '}
    {count === 1 ? 'that matches' : 'that match'} your current filters and
    search
  </>
)

// The export button of an index page whose file holds the rows its grid lists, across
// all pages. It asks first, stating what the file will hold.
export const useListedRowsExport = ({
  noun,
  count,
  isNarrowedByDefault = false,
  onExport,
}: {
  // Singular and lowercase, e.g. 'risk'.
  noun: string
  count: number
  // A filter narrows the list with nothing in the URL, through its default selection.
  isNarrowedByDefault?: boolean
  onExport: () => Promise<void> | void
}): { onClick: () => void; isDisabled: boolean; tooltipText: string } => {
  const isFiltered = useIsListNarrowed() || isNarrowedByDefault
  const title = `Export ${capitalizeFirstLetter(pluralize(noun))} to CSV`
  const openConfirmation = useConfirmationModal({
    title,
    body: (
      <p>
        Trustero will create a csv file of{' '}
        {isFiltered ? (
          <MatchingRowsText count={count} noun={noun} />
        ) : (
          <>
            {count === 1 ? 'the' : 'all'} <strong>{count}</strong>{' '}
            {pluralize(noun, count)}
          </>
        )}
        .
      </p>
    ),
    confirmText: 'Export CSV',
    onConfirmCB: onExport,
  })
  // Also covers the grid loading, when it briefly lists nothing.
  const isDisabled = !count

  return {
    onClick: () => openConfirmation(),
    isDisabled,
    tooltipText: isDisabled ? `No ${pluralize(noun)} to export` : title,
  }
}
